import { Card, CardContent, Checkbox, Collapse, Divider, Drawer, FormControl, FormLabel, Grid, Popover, Tooltip, Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import GetAppIcon from '@material-ui/icons/GetApp';
import WatchLaterIcon from '@material-ui/icons/WatchLater';
import { conciliacaoStatusDescriptions } from 'common/genericDescriptions';
import { useGenericStyles } from 'common/genericStyles';
import { ConciliacaoCDFilter, ConciliacaoContaDepositoResponse, ConciliacaoStatus, PaginatedResult, Perfil } from 'common/genericTypes';
import { GenericToast, GenericToastKind } from 'components/genericToast';
import { WarningSign } from 'components/warningSign';
import { Button, MdSpace, SmSpace, TextButton, TextField } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  GETConciliacoesPaginated,
  GETConciliacaoContaDepositoDownload,
  GETConciliacaoContaDepositoDownloadPdf,
  POSTConfirmarConciliacaoContaDeposito,
  POSTConfirmarConciliacaoContaDepositoEmLote,
} from 'requests/investidor';
import { formatDate } from 'utils/date';
import { RootState } from 'store';
import { DataGridPro, GridCellParams, GridFilterModel, GridSortModel, GridValueGetterParams, getGridDateOperators, getGridStringOperators } from '@mui/x-data-grid-pro';
import { localizedTextsMap } from 'utils/dataGridUtils';
import { componentsPropsValues, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';

interface DownloadActionsProps {
  conciliacao: ConciliacaoContaDepositoResponse;
  isUsuarioLeitura: boolean;
  onDownloadExcel: (id: string, data: Date) => void;
  onDownloadPdf: (id: string, data: Date) => void;
}

const DownloadActions: React.FC<DownloadActionsProps> = ({
  conciliacao,
  isUsuarioLeitura,
  onDownloadExcel,
  onDownloadPdf,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  if (isUsuarioLeitura) return null;

  return (
    <>
      <Button
        startIcon={<GetAppIcon fontSize="small" />}
        style={{ padding: 0, borderRadius: 0 }}
        onClick={handleToggle}
      >
        {'Download'}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        PaperProps={{ style: { overflow: 'visible', marginLeft: 8 } }}
      >
        <Grid container spacing={1} style={{ padding: '4px 8px' }}>
          <Grid item>
            <Button
              startIcon={<GetAppIcon fontSize="small" />}
              style={{ padding: 0, borderRadius: 0, minWidth: 0 }}
              onClick={() => {
                onDownloadExcel(conciliacao.conciliacaoContaDepositoId, conciliacao.data);
                handleClose();
              }}
            >
              {'Excel'}
            </Button>
          </Grid>
          <Grid item>
            <Button
              startIcon={<GetAppIcon fontSize="small" />}
              style={{ padding: 0, borderRadius: 0, minWidth: 0 }}
              onClick={() => {
                onDownloadPdf(conciliacao.conciliacaoContaDepositoId, conciliacao.data);
                handleClose();
              }}
            >
              {'PDF'}
            </Button>
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

const ListaConciliacoes: React.FC = () => {
  const dispatch = useDispatch();
  const genericClasses = useGenericStyles();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = React.useState(20);
  const [pageCount, setPageCount] = useState<number>(0);

  const [conciliacoesRestantes, setConciliacoesRestantes] = useState<ConciliacaoContaDepositoResponse[]>([]);  
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [conciliacoes, setConciliacoes] = useState<ConciliacaoContaDepositoResponse[]>([]);
  const [conciliacaoFilter, setConciliacaoFilter] = useState<Array<ConciliacaoCDFilter>>([]);
  const [linkOperator, setLinkOperator] = useState<any>('AND');
  const [conciliacaoConfirmada, setConciliacaoConfirmada] = useState<ConciliacaoContaDepositoResponse>();
  
  const [filterDataFrom, setFilterDataFrom] = useState<string>('');
  const [filterDataTo, setFilterDataTo] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  
  const [batchMode, setBatchMode] = useState<boolean>(false);
  const [selectedConciliacoes, setSelectedConciliacoes] = useState<string[]>([]);
  const [openConfirmarConciliacaoLoteDrawer, setOpenConfirmarConciliacaoLoteDrawer] = useState<boolean>(false);
  const [conciliacaoStatusMap, setConciliacaoStatusMap] = useState<Record<string, 'success' | 'error' | 'pending'>>({});
  const [isProcessingBatch, setIsProcessingBatch] = useState<boolean>(false);

  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);

  const getStatusIcon = (atrasada: boolean, status: ConciliacaoStatus) => {
    if (atrasada) return <ErrorIcon fontSize="small" className={genericClasses.warningIcon} />;
    switch (status) {
      case ConciliacaoStatus.AguardandoConciliacao:
      case ConciliacaoStatus.AguardandoDownload:
        return <WatchLaterIcon fontSize="small" className={genericClasses.warningIcon} />;
      case ConciliacaoStatus.Conciliado:
        return <CheckCircleIcon fontSize="small" className={genericClasses.successIcon} />;
      default:
        return <></>;
    }
  };

  const getToolTipDescription = (atrasada: boolean, status: ConciliacaoStatus) => {
    if (atrasada) return 'Conciliação em Atraso';
    return conciliacaoStatusDescriptions[status];
  };

  const getConciliacoes = useCallback(async (preserveSelections = false) => {
    try {
      const currentSelections = preserveSelections ? [...selectedConciliacoes] : [];
      const currentStatusMap = preserveSelections ? {...conciliacaoStatusMap} : {};
      
      const payload = {
        page,
        pageSize,
        linkOperator,
        conciliacaoContaDepositoFilter: conciliacaoFilter
      }
      setShowLoader(true);
      const result = await GETConciliacoesPaginated(dispatch, payload, true);
      setConciliacoes(result.result);
      setPageCount(result.totalCount);
      
      if (preserveSelections) {
        setSelectedConciliacoes(currentSelections);
        setConciliacaoStatusMap(currentStatusMap);
      }
      
      setShowLoader(false);
    } catch (error) {

    }
  }, [dispatch, conciliacaoFilter, page, pageSize, linkOperator]);
  
  const handleSearch = () => {
    let newFilters: Array<ConciliacaoCDFilter> = [];
    
    if (filterDataFrom) {
      const dateParts = filterDataFrom.split('-');
      const formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
      
      newFilters.push({
        columnType: 'DATA',
        operationFilterType: 'ONORAFTER',
        value: formattedDate
      } as ConciliacaoCDFilter);
    }
    
    if (filterDataTo) {
      const dateParts = filterDataTo.split('-');
      const formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
      
      newFilters.push({
        columnType: 'DATA',
        operationFilterType: 'ONORBEFORE',
        value: formattedDate
      } as ConciliacaoCDFilter);
    }
    
    if (filterStatus) {
      let statusDisplayValue = filterStatus;
      
      if (filterStatus === ConciliacaoStatus.AguardandoConciliacao) {
        statusDisplayValue = 'AGUARDANDO CONCILIAÇÃO';
      } else if (filterStatus === ConciliacaoStatus.AguardandoDownload) {
        statusDisplayValue = 'AGUARDANDO DOWNLOAD';
      } else if (filterStatus === ConciliacaoStatus.Conciliado) {
        statusDisplayValue = 'CONCILIADO';
      }
      
      newFilters.push({
        columnType: 'STATUS',
        operationFilterType: 'CONTAINS',
        value: statusDisplayValue,
      } as ConciliacaoCDFilter);
    }
    
    setConciliacaoFilter(newFilters);
    setPage(0);
  };
  
  const handleClearFilters = () => {
    setFilterDataFrom('');
    setFilterDataTo('');
    setFilterStatus('');
    setConciliacaoFilter([]);
    setPage(0);
  };
  
  const handleToggleBatchMode = () => {
    setBatchMode(!batchMode);
    setSelectedConciliacoes([]);
  };
  
  const handleSelectConciliacao = (event: React.ChangeEvent<HTMLInputElement>, conciliacaoId: string) => {
    if (event.target.checked) {
      setSelectedConciliacoes([...selectedConciliacoes, conciliacaoId]);
    } else {
      setSelectedConciliacoes(selectedConciliacoes.filter(id => id !== conciliacaoId));
    }
  };
  
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const eligibleConciliacoes = conciliacoes
        .filter(c => isConciliacaoEligibleForBatch(c))
        .map(c => c.conciliacaoContaDepositoId);
      setSelectedConciliacoes(eligibleConciliacoes);
    } else {
      setSelectedConciliacoes([]);
    }
  };
  
  const isConciliacaoEligibleForBatch = (conciliacao: ConciliacaoContaDepositoResponse) => {
    return conciliacao.status === ConciliacaoStatus.AguardandoConciliacao || conciliacao.status === ConciliacaoStatus.AguardandoDownload;
  };
  
  const getSelectedConciliacaoObjects = () => {
    return conciliacoes.filter(conciliacao => 
      selectedConciliacoes.includes(conciliacao.conciliacaoContaDepositoId)
    );
  };
  
  const [batchConciliacaoConfirmada, setBatchConciliacaoConfirmada] = useState<boolean>(false);
  const [batchConciliacaoError, setBatchConciliacaoError] = useState<string>('');

  const [needsRefreshAfterClose, setNeedsRefreshAfterClose] = useState<boolean>(false);
  
  const handleConfirmBatchConciliacao = async () => {
    if (selectedConciliacoes.length === 0) return;
    
    try {
      setIsProcessingBatch(true);
      setShowLoader(true);
      
      const currentSelectedIds = [...selectedConciliacoes];
      
      const initialStatusMap: Record<string, 'success' | 'error' | 'pending'> = {};
      currentSelectedIds.forEach(id => {
        initialStatusMap[id] = 'pending';
      });
      setConciliacaoStatusMap(initialStatusMap);
      
      await POSTConfirmarConciliacaoContaDepositoEmLote(dispatch, currentSelectedIds);
      
      const successStatusMap: Record<string, 'success' | 'error' | 'pending'> = {};
      currentSelectedIds.forEach(id => {
        successStatusMap[id] = 'success';
      });
      setConciliacaoStatusMap(successStatusMap);
      
      setBatchConciliacaoConfirmada(true);
      
      setNeedsRefreshAfterClose(true);
    } catch (error) {
      console.error('Erro ao confirmar conciliações em lote:', error);
      setBatchConciliacaoError('Erro ao confirmar conciliações em lote');
      
      const errorStatusMap: Record<string, 'success' | 'error' | 'pending'> = {};
      selectedConciliacoes.forEach(id => {
        errorStatusMap[id] = 'error';
      });
      setConciliacaoStatusMap(errorStatusMap);
      
      setNeedsRefreshAfterClose(true);
    } finally {
      setShowLoader(false);
      setIsProcessingBatch(false);
    }
  };
  
  const handleCloseBatchDrawer = () => {
    setOpenConfirmarConciliacaoLoteDrawer(false);
    
    if (Object.keys(conciliacaoStatusMap).length > 0) {
      if (needsRefreshAfterClose) {
        getConciliacoes();
        setNeedsRefreshAfterClose(false);
      }
      
      setBatchMode(false);
      setSelectedConciliacoes([]);
      setConciliacaoStatusMap({});
    }
  };
  

  const handleConciliacaoDownload = async (conciliacaoContaDepositoId: string, data: Date) => {
    try {
      const result = await GETConciliacaoContaDepositoDownload(dispatch, conciliacaoContaDepositoId, true);
      const blob = new Blob([result], { type: 'blob' });
      const link = document.createElement('a');
      const href = window.URL.createObjectURL(blob);
      link.href = href;
      link.download = `conciliacao_${formatDate(data, 'DDMMYYYY')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(href);
      getConciliacoes();
    } catch (error) {}
  };

  const handleConciliacaoDownloadPdf = async (conciliacaoContaDepositoId: string, data: Date) => {
    try {
      const result = await GETConciliacaoContaDepositoDownloadPdf(dispatch, conciliacaoContaDepositoId, true);

      const response = await fetch(result.url);
      if (!response.ok) {
        throw new Error('Erro ao buscar o arquivo PDF de conciliação');
      }

      const blob = await response.blob();
      const link = document.createElement('a');
      const href = window.URL.createObjectURL(blob);
      link.href = href;
      link.download = `conciliacao_${formatDate(data, 'DDMMYYYY')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(href);

      getConciliacoes();
    } catch (error) {}
  };

  useEffect(() => {
    getConciliacoes();
  }, [getConciliacoes]);

  const onFilterChange = useCallback(
    (filterModel: GridFilterModel) => {
      if (!filterModel.items) {
        setConciliacaoFilter([]);
        return;
      }
      setLinkOperator(filterModel?.linkOperator?.toString()?.toUpperCase() ?? 'AND');
      var conciliacaoFilter = new Array<ConciliacaoCDFilter>();

      filterModel.items.forEach((filter) => {
        let operatorValue =
          filter.operatorValue == 'startsWith'
            ? 'STARTS_WITH'
            : filter.operatorValue == 'endsWith'
            ? 'ENDS_WITH'
            : filter.operatorValue?.toUpperCase();

        let formattedValue = filter.value?.toString();
        if (formattedValue) {
          if (filter.columnField == 'tipo') {
            formattedValue = formattedValue.replace('_', ' ');
          }
          if(filter.columnField == 'DATA'){
            formattedValue = formattedValue.split('-');
            formattedValue = `${formattedValue[2]}/${formattedValue[1]}/${formattedValue[0]}`;
          }

          let params = {
            columnType: filter.columnField.toUpperCase(),
            operationFilterType: operatorValue,
            value: formattedValue,
          } as ConciliacaoCDFilter;

          conciliacaoFilter.push(params);
        }
      });

      setConciliacaoFilter(conciliacaoFilter);
    },
    [getConciliacoes, page, pageSize, linkOperator, conciliacaoFilter]
  );

  const onSortChange = useCallback(
    (filterModel: GridSortModel) => {
      if (filterModel == null) return;

      var filter = filterModel[0];
      let params = {
        columnType: filter.field.toUpperCase(),
        orderByType: filter.sort?.toUpperCase(),
      } as ConciliacaoCDFilter;

      var newEFFilter = conciliacaoFilter.filter(
        (x) => x.orderByType != 'ASC' && x.orderByType != 'DESC'
      ) as Array<ConciliacaoCDFilter>;
      newEFFilter.push(params);
      setConciliacaoFilter([...newEFFilter]);
    },
    [getConciliacoes, page, pageSize, conciliacaoFilter]
  );
  
  const sizeColumns = 300;
  const minSizeColumns = 200;
  const maxSizeColumns = 300;

  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ['equals', 'contains', 'startsWith', 'endsWith'].includes(value)
  );

  const filterOperatorsData = getGridDateOperators().filter(({value}) => 
    ["is", "not", "after", "onOrAfter", "before", "onOrBefore"].includes(value)
  );
  
  const getGridColumns = useCallback(
    () => [
      ...(batchMode
        ? [
          {
            field: 'selection',
            headerName: ' ',
            width: 75,
            minWidth: 75,
            sortable: false,
            filterable: false,
            disableExport: true,
            renderHeader: () => (
              <Checkbox
                color="primary"
                indeterminate={
                  selectedConciliacoes.length > 0 &&
                  selectedConciliacoes.length < conciliacoes.filter(isConciliacaoEligibleForBatch).length
                }
                checked={
                  conciliacoes.filter(isConciliacaoEligibleForBatch).length > 0 &&
                  selectedConciliacoes.length === conciliacoes.filter(isConciliacaoEligibleForBatch).length
                }
                onChange={handleSelectAll}
              />
            ),
            renderCell: (params: GridCellParams) => {
              const conciliacao = params.row as ConciliacaoContaDepositoResponse;
              const isEligible = isConciliacaoEligibleForBatch(conciliacao);

              return (
                <Checkbox
                  color="primary"
                  checked={selectedConciliacoes.includes(conciliacao.conciliacaoContaDepositoId)}
                  onChange={(event) => handleSelectConciliacao(event, conciliacao.conciliacaoContaDepositoId)}
                  disabled={!isEligible}
                />
              );
            },
          },
        ]
        : []),
      {
        field: 'STATUS',
        headerName: 'Status',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: sizeColumns,
        minWidth: minSizeColumns,
        maxWidth: maxSizeColumns,
        valueGetter: (params: GridValueGetterParams) => (params.row as ConciliacaoContaDepositoResponse).status,
        renderCell: (params: GridCellParams) => {
          const conciliacao = params.row as ConciliacaoContaDepositoResponse;
          return (
            <>
             <Tooltip
                title={getToolTipDescription(conciliacao.atrasada, conciliacao.status)}
                aria-label={getToolTipDescription(conciliacao.atrasada, conciliacao.status)}
              >
                {getStatusIcon(conciliacao.atrasada, conciliacao.status)}
              </Tooltip>             
            </>
          );
        },
      },
      {
        field: 'DATA',
        headerName: 'Data',
        type: 'date',
        sortable: true,
        filterable: true,
        filterOperators: filterOperatorsData,
        disableExport: false,
        width: sizeColumns,
        minWidth: minSizeColumns,
        maxWidth: maxSizeColumns,
        valueGetter: (params: GridValueGetterParams) => (params.row as ConciliacaoContaDepositoResponse).data,
        renderCell: (params: GridCellParams) => {
          const conciliacao = params.row as ConciliacaoContaDepositoResponse;
          return (
            <>
            <Typography variant="body1" display={'inline'}>
              {formatDate(conciliacao.data)}
            </Typography>
            </>
          );
        },
      },
      {
        field: 'Download',
        headerName: 'Download & Confirmar',
        type: 'string',
        width: sizeColumns,
        minWidth: minSizeColumns,
        maxWidth: maxSizeColumns,
        valueGetter: (params: GridValueGetterParams) => (params.row as ConciliacaoContaDepositoResponse),
        renderCell: (params: GridCellParams) => {
          const conciliacao = params.row as ConciliacaoContaDepositoResponse;
          return (
            <DownloadActions
              conciliacao={conciliacao}
              isUsuarioLeitura={isUsuarioLeitura}
              onDownloadExcel={handleConciliacaoDownload}
              onDownloadPdf={handleConciliacaoDownloadPdf}
            />
          );
        },
      }
    ],
    [batchMode, selectedConciliacoes, conciliacoes]
  );

  return (
    <>
      {!!conciliacaoConfirmada && (
        <>
          <GenericToast kind={GenericToastKind.Success} onDismiss={() => setConciliacaoConfirmada(undefined)}>
            <Typography variant="body2" display={'inline'}>
              {'Conciliação de'}
            </Typography>
            &nbsp;
            <Typography display={'inline'}>{formatDate(conciliacaoConfirmada.data)}</Typography>
            &nbsp;
            <Typography variant="body2" display={'inline'}>
              {'confirmada com sucesso'}
            </Typography>
          </GenericToast>
          <MdSpace />
        </>
      )}
      
      {!!batchConciliacaoConfirmada && (
        <>
          <GenericToast kind={GenericToastKind.Success} onDismiss={() => setBatchConciliacaoConfirmada(false)}>
            <Typography variant="body2" display={'inline'}>
              {'Conciliações em lote confirmadas com sucesso!'}
            </Typography>
          </GenericToast>
          <MdSpace />
        </>
      )}
      
      {!!batchConciliacaoError && (
        <>
          <GenericToast kind={GenericToastKind.Error} onDismiss={() => setBatchConciliacaoError('')}>
            <Typography variant="body2" display={'inline'}>
              {batchConciliacaoError}
            </Typography>
          </GenericToast>
          <MdSpace />
        </>
      )}
      
      {/* Search Bar */}
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: '16px' }}>
        <Grid container item xs={10}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Typography>{'Data da Conciliação'}</Typography>
            </Grid>
            <Grid item>
              <FormControl fullWidth>
                <FormLabel>De</FormLabel>
                <TextField
                  type="date"
                  value={filterDataFrom}
                  onChange={(newDateFrom) => {
                    setFilterDataFrom(newDateFrom);
                    
                    if (filterDataTo && filterDataTo < newDateFrom) {
                      setFilterDataTo(newDateFrom);
                    }
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl fullWidth>
                <FormLabel>Até</FormLabel>
                <TextField
                  type="date"
                  value={filterDataTo}
                  onChange={(newDateTo) => setFilterDataTo(newDateTo)}
                  inputProps={{ min: filterDataFrom || undefined }}
                />
              </FormControl>
            </Grid>
            <Grid item>
              <Typography>{'Status'}</Typography>
            </Grid>
            <Grid item>
              <FormControl>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{ padding: '8px', minWidth: '200px' }}
                >
                  <option value="">Todos</option>
                  <option value={ConciliacaoStatus.AguardandoDownload}>Aguardando Download</option>
                  <option value={ConciliacaoStatus.AguardandoConciliacao}>Aguardando Conciliação</option>
                  <option value={ConciliacaoStatus.Conciliado}>Conciliado</option>
                </select>
              </FormControl>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                type="button"
                style={{ marginRight: '8px' }}
              >
                {'Pesquisar'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                type="button"
              >
                {'Limpar Filtros'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid container item xs={2} justifyContent="flex-end">
          {!isUsuarioLeitura && (
            !batchMode ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleToggleBatchMode}
              >
                {'Conciliação em Lote'}
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="default"
                  onClick={handleToggleBatchMode}
                  style={{ marginRight: '8px' }}
                >
                  {'Cancelar'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={selectedConciliacoes.length === 0}
                  onClick={() => setOpenConfirmarConciliacaoLoteDrawer(true)}
                >
                  {'Confirmar'}
                </Button>
              </>
            )
          )}
        </Grid>
      </Grid>
      
      <DataGridPro
        getRowId={(row)=> row.conciliacaoContaDepositoId}
        autoHeight
        localeText={localizedTextsMap}
        componentsProps={componentsPropsValues}
        rows={conciliacoes}
        columns={getGridColumns()}
        disableSelectionOnClick
        rowsPerPageOptions={rowsPerPageOptionsValues}
        page={page}
        rowCount={pageCount}
        pagination
        paginationMode="server"
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize), setPage(0);
        }}
        pageSize={pageSize}
        loading={showLoader}
        filterMode="server"
        onFilterModelChange={onFilterChange}
        sortingMode="server"
        onSortModelChange={onSortChange}
        
      />
      <Drawer
        anchor="right"
        open={openConfirmarConciliacaoLoteDrawer}
        onClose={handleCloseBatchDrawer}
        PaperProps={{ style: { padding: '40px', width: 465 } }}
      >
        <Grid container>
          <Grid item md={12}>
            <Typography variant="h3">{'Confirmar Conciliação em Lote'}</Typography>
            <SmSpace />
            <Divider variant="fullWidth" />
            <SmSpace />
            <Typography variant="body2">{`Tem certeza que deseja confirmar a conciliação de ${selectedConciliacoes.length} item(s)?`}</Typography>
            <SmSpace />
            
            {/* Table of selected conciliations */}
            <Card variant="outlined" style={{ marginBottom: '16px' }}>
              <CardContent style={{ padding: '16px' }}>
                <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>Conciliações selecionadas:</Typography>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e0e0e0' }}>Data</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e0e0e0' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSelectedConciliacaoObjects().map((conciliacao) => (
                        <tr key={conciliacao.conciliacaoContaDepositoId}>
                          <td style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                            {formatDate(conciliacao.data)}
                          </td>
                          <td style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                            {conciliacaoStatusMap[conciliacao.conciliacaoContaDepositoId] === 'success' && (
                              <CheckCircleIcon fontSize="small" className={genericClasses.successIcon} />
                            )}
                            {conciliacaoStatusMap[conciliacao.conciliacaoContaDepositoId] === 'error' && (
                              <ErrorIcon fontSize="small" className={genericClasses.errorIcon} />
                            )}
                            {conciliacaoStatusMap[conciliacao.conciliacaoContaDepositoId] === 'pending' && (
                              <WatchLaterIcon fontSize="small" className={genericClasses.warningIcon} />
                            )}
                            {!conciliacaoStatusMap[conciliacao.conciliacaoContaDepositoId] && (
                              <span>-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            
            {(isProcessingBatch || Object.keys(conciliacaoStatusMap).length > 0) && (
              <Card 
                variant="outlined" 
                style={{ 
                  marginBottom: '16px', 
                  backgroundColor: isProcessingBatch 
                    ? '#fff8e1' 
                    : Object.values(conciliacaoStatusMap).every(status => status === 'success') 
                      ? '#e8f5e9' 
                      : '#ffebee'  
                }}
              >
                <CardContent style={{ padding: '16px' }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      {isProcessingBatch 
                        ? <WatchLaterIcon className={genericClasses.warningIcon} />
                        : Object.values(conciliacaoStatusMap).every(status => status === 'success') 
                          ? <CheckCircleIcon className={genericClasses.successIcon} /> 
                          : <ErrorIcon className={genericClasses.errorIcon} />}
                    </Grid>
                    <Grid item xs>
                      <Typography variant="body1" style={{ fontWeight: 500 }}>
                        {isProcessingBatch
                          ? 'Processamento em andamento...'
                          : Object.values(conciliacaoStatusMap).every(status => status === 'success')
                            ? 'Processamento concluído com sucesso!'
                            : 'Processamento concluído com erros!'}
                      </Typography>
                      <Typography variant="body2">
                        {isProcessingBatch
                          ? 'As conciliações estão sendo processadas. Por favor, aguarde.'
                          : Object.values(conciliacaoStatusMap).every(status => status === 'success')
                            ? 'Todas as conciliações foram confirmadas com sucesso.'
                            : 'Ocorreram erros durante a confirmação de algumas conciliações. Por favor, verifique o status acima.'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
            
            <WarningSign message="Ao clicar em confirmar, o Participante declara, desde já, que a Laqus forneceu todas as informações necessárias, bem como adotou todos os procedimentos para assegurar a Conciliação Diária, nos termos do Art. 4°, § 4º, Inciso II e do Art. 38 da Seção III – Conciliação, da ICVM 541." />
            <SmSpace />
            <Button 
              onClick={handleConfirmBatchConciliacao} 
              variant={'contained'} 
              color={'primary'} 
              fullWidth
              disabled={isProcessingBatch || Object.keys(conciliacaoStatusMap).length > 0}
            >
              {isProcessingBatch ? 'Processando...' : 'Confirmar'}
            </Button>
            <SmSpace />
            <Button 
              onClick={handleCloseBatchDrawer} 
              variant={'outlined'} 
              fullWidth
              disabled={isProcessingBatch}
            >
              {Object.keys(conciliacaoStatusMap).length > 0 ? 'Retornar' : 'Cancelar'}
            </Button>
          </Grid>
        </Grid>
      </Drawer>
    </>
  );
};

export default ListaConciliacoes;
